import { Affix, Avatar, Button, Dropdown, MenuProps, Switch, Tooltip } from 'antd';
import Sider from 'antd/es/layout/Sider';
import classes from './SideBarMenu.module.scss';
import LogoType from 'assets/img';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { BarChartOutlined, CalculatorOutlined, MoonOutlined, GlobalOutlined, LogoutOutlined, QuestionCircleOutlined, SunOutlined, UserOutlined, HighlightOutlined } from '@ant-design/icons';
import { setIsDarkMode, setIsProfileOpen, setLogoutInProcess, setSelectedKeys, setSoundNotificationState } from 'store/slices/windowStateSlice';
import { resetTokensAndSendToLogin } from 'helpers/cookies';
import { setRooms, setUserData } from 'store/slices/userDataSlice';
import Assests from 'assets/icons';
import React from 'react';
import SupportModal from 'components/Modals/SupportModal/SupportModal';
import Notifications from './Notifications/Notifications';
import cn from 'classnames';
import { ConfirmationModal, ProfileModal } from 'components/Modals';
import { languagesValues } from 'routes/Main/Main';
import auth from 'api/auth';
import { clearReduxFunction } from 'store/reducers/userDataCreator';
import { 
  clearUploadingDocuments, 
  clearUploadingFolders, 
  setCanceledUploads,
  setUploadAbortController, 
  setViewType
} from 'store/slices/dataDocumentsSlice';
import { resetNotifications } from 'store/slices/dataRoomSlice';
import { resetChatState } from 'store/slices/chatsSlice';
import ResearchUploadModal from 'components/Modals/ResearchUploadModal/ResearchUploadModal';
import { setIsResearchModalOpen } from 'store/slices/researchSlice';
import { getTheme } from 'helpers/getTheme';
import api from 'api';
import Spinner from 'components/Spinner/Spinner';
import { checkIsTariffEnded, checkIsAwaitingPayment } from 'helpers/tariffPayment';
import { serializeFiltersToUrl } from 'helpers/documentFiltersUrl';

const authType = window.REACT_APP_AUTH_TYPE !== 'REPLACE_REACT_APP_AUTH_TYPE' ? window.REACT_APP_AUTH_TYPE : process.env.REACT_APP_AUTH_TYPE; 
const loginAdress = authType === 'blitz' ? 'blitz-auth' : 'login';

const SideBarMenu = () => {
  const [isSupportModalOpen, setIsSupportModalOpen] = React.useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = React.useState(false);
  const [isOpenDropdown, setIsOpenDropdown] = React.useState(false);
  const [isSupportChatLoading, setIsSupportChatLoading] = React.useState(false);

  const { selectedKeys, isDarkMode, messageApi, isProfileOpen, logoutInProcess } = useAppSelector((store) => store.windowState);
  const { userData } = useAppSelector((store) => store.userData);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { isResearchModalOpen } = useAppSelector((store) => store.research);
  const { globalChats } = useAppSelector((store) => store.chat);
  const { appliedFilters } = useAppSelector((store) => store.documents);
  const defaultLanguage = localStorage.getItem('language') || 'en';
  const body = document.querySelector('body');  

  const { t, i18n } = useTranslation();
  
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();

  const isRoomPage = selectedKeys.includes('rooms') || selectedKeys.includes('documents') || selectedKeys.includes('/documents')
    || selectedKeys.includes('logs') || selectedKeys.includes('settings') || selectedKeys.includes('nda');

  const toggleTheme = (checked: boolean) => {
    dispatch(setIsDarkMode(checked));
  };

  const openSupportBot = () => {
    const url = 'https://t.me/athena_support_bot?start=support';
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openSupportChat = async () => {
    const supportChat = globalChats.find((chat) => chat.entity_type === 5);

    if (supportChat) {
      navigate(`/global-chats/${supportChat.id}/`);
      return;
    }

    try {
      setIsSupportChatLoading(true);
      const chats = (await api.getGLobalChatsV2()).data;
      const found = chats.find((chat) => chat.entity_type === 5);
      if (found) navigate(`/global-chats/${found.id}/`);
    } catch (err) {
      messageApi?.error(t('Chats.supportChatError'));
    } finally {
      setIsSupportChatLoading(false);
    }
  };
  
  React.useEffect(() => {
    if (localStorage.getItem('theme') === 'dark') {
      body?.classList.add('dark');
    }
  }, [body])

  const logoutHandle = async () => {
    dispatch(setLogoutInProcess(true));
    localStorage.removeItem('alreadyDeniedSound');
    dispatch(setSoundNotificationState({ open: false, enabled: false, denied: false }));
    dispatch(resetNotifications());
    
    await resetTokensAndSendToLogin();
    dispatch(clearReduxFunction({}));
    dispatch(setUserData(null));
    dispatch(setRooms([]));
    dispatch(setSelectedKeys([`/${loginAdress}`]));

    // Очистка состояний загрузки документов
    dispatch(clearUploadingDocuments());
    dispatch(clearUploadingFolders());
    dispatch(setCanceledUploads([]));
    dispatch(setUploadAbortController(null));

    dispatch(resetChatState());
    setTimeout(() => {
      dispatch(setLogoutInProcess(false));
      setLogoutConfirmOpen(false);
    }, 3000);
  };

  const onCancel = () => dispatch(setIsProfileOpen(false));

  const onResetHandle = async () => {
    try {
      await auth.sendEmailToRestore({email: userData?.email!});
      messageApi?.success(`${t('resetPassword.successSend')} ${userData?.email}`);
    } catch (err) {
      messageApi?.error(t('resetPassword.failedSend'));
    }
  };

  const switchThemeElement = () => (
    <Switch
      checkedChildren={<SunOutlined />}
      unCheckedChildren={<MoonOutlined />}
      checked={isDarkMode}
      onChange={toggleTheme}
      style={{ marginBottom: 12 }}
    />
  );

  const onOpenChange = (open: boolean, info: { source: 'trigger' | 'menu' }) => {    
    info.source === 'trigger' && setIsOpenDropdown(open);
  }

  const onClickItem: MenuProps['onClick'] = async ({ key }) => {
    if (key === 'theme') {
      toggleTheme(!isDarkMode);
      return;
    }

    setIsOpenDropdown(false);
    const languages = languagesValues.map(el => el.value);
    if (languages.includes(key)) {
      localStorage.setItem('language', key);
      i18n.changeLanguage(key);
      return;
    }
    if (key === '/logout') return setLogoutConfirmOpen(true);
    // else if (key === 'support') return setIsSupportModalOpen(true); // TODO: доделать в модалке вложения и потом вернуть, дополнительно добавить кнопку со ссылкой на бота
    else if (key === 'support_telegram') return openSupportBot();
    else if (key === 'support_chat') return openSupportChat();
    else if (key === 'profile') return dispatch(setIsProfileOpen(true));
    else if (key === 'report'){
      dispatch(setIsResearchModalOpen(true));
      return;
    }
    else if (key === 'change_password') {
      await onResetHandle();
      return;
    }

    if (key !== 'ref') localStorage.setItem('previousPage', key);
    navigate(key, { state: pathname });
    dispatch(setSelectedKeys([key]));
  };

  const onChangeSection = (key: string) => {
    localStorage.setItem('prevTab', key);
    dispatch(setSelectedKeys([key]));
    
    const isUnpaidRoom = checkIsAwaitingPayment(dataRoom);
    const isEndedTariff = checkIsTariffEnded(dataRoom);
    const isNeedToAccessTerms = dataRoom?.access_terms_status == 'waiting_for_approve';

    if (key === 'rooms') {
      if (dataRoom?.id) {        
        if (isUnpaidRoom) {
          navigate(`/room/${dataRoom?.id}/awaitingPayment`);
        } else if (isEndedTariff) {
          navigate(`/room/${dataRoom.custom_url || dataRoom?.id}/endedTariff`);
        } else {
          const filterParams = serializeFiltersToUrl(new URLSearchParams(), appliedFilters).toString();
          const filterQuery = filterParams ? `?${filterParams}` : '';
          dataRoom?.access_terms_status == 'waiting_for_approve'
            ? navigate(`/room/${dataRoom.custom_url || dataRoom?.id}/nda`)
            : navigate(`/room/${dataRoom.custom_url || dataRoom?.id}/documents${filterQuery}`);
        }
      } else {
        navigate(`/${key}`);
      }
    } else if (key === 'chat') {
      dispatch(setViewType('Table'));
      if (dataRoom?.id && !isUnpaidRoom && !isEndedTariff && !isNeedToAccessTerms) navigate(`/room/${dataRoom.custom_url || dataRoom?.id}/${key}`);
      else navigate(`/global-chats/`);
    } else if (key === 'organization')  {
      navigate(`/${key}`)
    };
  };

  const profileItems: MenuProps['items'] = [
    {
      label: t('sidebar.profile.profile'),
      key: 'profile',
      title: '',
      icon: <UserOutlined style={{ fontSize: 16 }} />,
      className: classes.labelPoint,
    },
    {
      label: t('sidebar.profile.analytics'),
      key: 'data-analytics',
      title: '',
      icon: <BarChartOutlined style={{ fontSize: 16 }} />,
      className: classes.labelPoint,
    },
    {
      label: t('sidebar.profile.change_password'),
      key: 'change_password',
      title: '',
      icon: <Assests.PasswordIcon />,
      className: classes.labelPoint,
    },
    // {
    //   label: t('sidebar.profile.organization'),
    //   key: 'organization',
    //   title: '',
    //   icon: <FontAwesomeIcon icon={faSuitcase} />,
    //   className: classes.labelPoint,
    // },
    // {
    //   label: t('sidebar.profile.operations'),
    //   key: 'billing',
    //   title: t('sidebar.profile.operations'),
    //   icon: <BarChartOutlined style={{ fontSize: 16 }} />,
    //   className: classes.labelPoint,
    // },
    {
      label: t('sidebar.report'),
      key: 'report',
      title: '',
      icon: <CalculatorOutlined style={{ fontSize: 16 }} />,
      className: classes.labelPoint,
    },
    {
      label: t('sidebar.profile.support'),
      key: 'support',
      title: '',
      icon: <QuestionCircleOutlined style={{ fontSize: 16 }} />,
      children: [
        {
          label: t('sidebar.profile.supportOptions.telegram'),
          key: 'support_telegram',
          icon: <Assests.TelegramIcon />,
          className: cn(classes.labelPoint, classes.telegram),
        },
        {
          label: t('sidebar.profile.supportOptions.chat'),
          key: 'support_chat',
          icon: <Assests.ChatIcon />,
          className: classes.labelPoint,
        },
      ],
      className: classes.labelPoint,
    },
    {
      label: t('sidebar.profile.faq'),
      key: 'ref',
      title: '',
      icon: <QuestionCircleOutlined style={{ fontSize: 16 }} />,
      className: classes.labelPoint,
    },
    {
      label: <div className={classes.switch}>
        {t('sidebar.profile.theme')}
        {switchThemeElement()}
      </div>,
      key: 'theme',
      title: '',
      icon: <HighlightOutlined style={{ fontSize: 16 }} />,
      className: classes.labelPoint,
    },
    {
      label: t('sidebar.profile.language'),
      key: 'language',
      title: '',
      icon: <GlobalOutlined style={{ fontSize: 16 }} />,
      children: languagesValues.map(el => ({
        ...el,
        key: el.value,
        className: classes.labelPoint,
      })),
      className: classes.labelPoint,
    },
    {
      type: 'divider',
    },
    {
      label: t('sidebar.profile.logout'),
      key: '/logout',
      title: '',
      icon: <LogoutOutlined style={{ fontSize: 16 }} />,
      className: classes.labelPoint,
    },
  ];

  return (
    <Affix>
      <Sider
        collapsed={true}
        className={classes.sideBarMenu}
        collapsedWidth={56}
      >
        <div className={classes.siderWrap}>
          
          <div className={classes.subWrap}>
            <img className={classes.logo} src={LogoType} alt='Logotype' />
            <Tooltip placement='right' title={t('sidebar.shortRooms')}>
              <Button
                onClick={() => onChangeSection('rooms')}
                className={cn(classes.button, isRoomPage ? classes.active : 'null')}
                type='text'
                icon={<Assests.RoomsIcon />}
              />
            </Tooltip>

            <Tooltip placement='right' title={t('sidebar.contacts')}>
              <Button
                onClick={() => onChangeSection('contacts')}
                className={cn(classes.button, selectedKeys.includes('contacts') ? classes.active : 'null')}
                type='text'
                icon={<Assests.ContactsIcon />}
              />
            </Tooltip>

            <Tooltip placement='right' title={t('sidebar.chats')}>
              <Button
                onClick={() => onChangeSection('chat')}
                id='all-chats-btn'
                className={cn(classes.button, selectedKeys.includes('chat') ? classes.active : 'null')}
                type='text'
                icon={<Assests.ChatIcon />}
              />
            </Tooltip>
          </div>

          <div className={classes.bottomSubWrap}>
            
            <Notifications />

            <Dropdown
              destroyOnHidden={true}
              open={isOpenDropdown}
              onOpenChange={onOpenChange}
              classNames={{ root: getTheme() }}
              menu={{
                items: profileItems,
                onClick: onClickItem,
                selectable: true,
                selectedKeys: [...selectedKeys, defaultLanguage],
                rootClassName: getTheme(),
              }}
              disabled={isSupportChatLoading}
            >
              {isSupportChatLoading ? (
                <div className={classes.spinnerWrap}>
                  <Spinner size={20} />
                </div>
              ) : (
                <Avatar className={classes.profile} >
                  <span className={classes.profileWord}>
                    {userData ? userData.first_name[0] : ''}
                  </span>
                </Avatar>
              )}
            </Dropdown>
          </div>

        </div>
        {isSupportModalOpen && (
          <React.Suspense fallback={<div />}>
            <SupportModal
              isSupportModalOpen={isSupportModalOpen}
              onCancel={() => setIsSupportModalOpen(false)}
            />
          </React.Suspense>
        )}

        {isProfileOpen && (
          <React.Suspense fallback={<div />}>
            <ProfileModal open={isProfileOpen} onCancel={onCancel} />
          </React.Suspense>
        )}
        {isResearchModalOpen && (
          <ResearchUploadModal
            isOpen={isResearchModalOpen}
            onCancel={() => dispatch(setIsResearchModalOpen(false))}
            t={t}
          />
        )}

        {logoutConfirmOpen && (<React.Suspense fallback={<div />}>
          <ConfirmationModal
            isOpen={logoutConfirmOpen}
            onCancel={() => setLogoutConfirmOpen(false)}
            isLoading={logoutInProcess}
            submit={logoutHandle}
            cancelText={t('Documents.management.cancel')}
            customClassname={'logout'}
            text={t('logout.text')}
            title={t('logout.title')}
            okText={t('logout.okBtn')}
          />
        </React.Suspense>)}
      </Sider>
    </Affix>
  );
}

export default SideBarMenu;
